import {
  Archive,
  BookOpen,
  BrickWall,
  FlaskConical,
  History,
  Hourglass,
  LayoutTemplate,
  Radar,
  Server,
  SquareTerminal,
  Users,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { Callout } from '@/modules/marketing/AnnotatedFigure';

/**
 * The task guides.
 *
 * ## Why these are not the feature articles
 *
 * A feature article answers "what does this do, and should I buy it". A guide answers "I am looking
 * at this screen right now, where do I click". They read differently, they are found differently,
 * and mixing them produces a page that serves neither: a customer mid-task does not want the
 * reasoning, and somebody evaluating the product does not want step four.
 *
 * ## Every step names a real control
 *
 * Buttons and sections are named exactly as the product labels them, so a customer comparing the
 * guide with their own screen finds the same words. When a label changes in the product, the guide
 * changes in the same commit.
 *
 * ## Screenshots are optional, and only ever real
 *
 * A step may carry a capture of the actual screen with markers over it (`image`, `callouts`). The
 * markers are positioned in percentages over that capture, so when a screen changes its shot has to be
 * recaptured, its dimensions re-read and its markers re-measured. A guide with no picture is better
 * than one whose picture shows a screen that no longer exists — which is why the guides written for
 * automation carry none until they are captured from the running product.
 *
 * ## `x`/`y` mark the control, `place` keeps the number off it
 *
 * Each coordinate is the point being named. Each `place` is the side that has free space beside it,
 * decided by looking at that particular screenshot.
 */

export interface GuideStep {
  /** The instruction, imperative and specific. */
  title: string;
  /** Two or three sentences at most. Somebody mid-task is not reading an essay. */
  body: string;
  /** Path under `public/`. Every one must exist; a broken figure is worse than no figure. */
  image?: string;
  /**
   * The screenshot's real pixel size, so the browser reserves the space before it arrives.
   *
   * A guide is several figures tall. Without these the page grows under the reader as each one loads
   * and they lose the step they were on.
   */
  width?: number;
  height?: number;
  /** What the figure is showing, for anybody who cannot see it. */
  caption?: string;
  callouts?: Callout[];
}

export interface Guide {
  slug: string;
  icon: LucideIcon;
  /** The heading and navigation label: the task, in a few words ("Set Up Backups"). */
  title: string;
  /**
   * The `<title>` and search-result heading, without the site name. Phrased the way the task is
   * searched for ("How to Set Up Automatic Server Backups"), which the short heading is not.
   */
  seoTitle: string;
  /** One line, shown in the index and in the documentation navigation. */
  summary: string;
  /** What has to be true before starting. Honest about ordering. */
  before: string;
  /**
   * Slugs this guide used to answer on.
   *
   * A renamed guide is still linked from somebody's notes, and a 404 is a worse answer than the page
   * they were looking for under a name it no longer uses. Only for the same task under an old name —
   * never to send a removed feature's guide somewhere unrelated.
   */
  aliases?: string[];
  steps: GuideStep[];
}

export const GUIDES: Guide[] = [
  {
    slug: 'start-a-free-trial',
    icon: Hourglass,
    title: 'Start a Free Trial',
    seoTitle: 'How the 7-Day Free Trial Works',
    summary:
      'What the 7-day free trial includes, where to see the days and allowances left, and what happens when you choose a plan or it ends.',
    before: 'An email address, or a Google account.',
    steps: [
      {
        title: 'Create your account',
        body: 'Sign up with your email address and confirm it from the email that follows, or sign in with Google. The trial starts at that moment, with no card. An older account that has never had a subscription can start one with the Start free trial button in the dashboard.',
      },
      {
        title: 'Check what the trial includes',
        body: 'The Free Trial panel on the dashboard shows the days left and how much of each allowance is used: 1 server, 3 monitors, 2 backup sources and 25 Command Runner runs. Playbooks, templates, Auto-Fix, backups and restore tests are all included, and the trial is for one person. During the trial you cannot download an archive through Techie Gain: that needs a paid plan. Everything else about backups works, and the archive is in your own storage, where you can fetch it yourself.',
      },
      {
        title: 'Connect the server to try it on',
        body: 'The trial allows one server, so connect the one you want to automate, monitor and back up. Removing it frees the place for a different server.',
      },
      {
        title: 'Choose a plan when you are ready',
        body: 'Billing lists the plans at any time during the trial. The trial ends as soon as a plan is active, and everything you set up carries on with that plan’s limits. You are emailed three days and one day before the trial ends.',
      },
      {
        title: 'Know what happens if it ends',
        body: 'Nothing is deleted, and your server keeps running. Monitor checks, scheduled backups and restore tests pause and new runs cannot be started until you choose a plan, which picks everything up where it stopped.',
      },
    ],
  },
  {
    slug: 'connect-a-server',
    icon: Server,
    title: 'Connect a Server',
    seoTitle: 'How to Connect a Linux Server over SSH',
    summary:
      'Connect a Linux server you already run, from any provider, as root or a user with sudo, and check what the platform detects about it.',
    before: 'A Linux server you can reach over SSH, and a password or key for an account on it.',
    steps: [
      {
        title: 'Open Servers and choose Add server',
        body: 'Servers is under Infrastructure in the sidebar. Next to the button you can see how many servers your plan allows and how many remain. Saved credentials are on the Credentials tab and can be reused.',
      },
      {
        title: 'Enter the address and username',
        body: 'Enter the hostname or IP address, the SSH port if it is not 22, and the username. The account can be root or any user with sudo; the platform detects which when it connects.',
      },
      {
        title: 'Add a password or SSH key',
        body: 'Both are encrypted before they are stored and are never shown again. If the account signs in with a key but needs a password for sudo, enter the sudo password as well. It is stored the same way and passed to sudo on standard input, never on the command line.',
      },
      {
        title: 'Save and review the detected details',
        body: 'Saving connects immediately. The server’s row shows the distribution, architecture, package manager, CPU, memory and disk, and a badge showing how the platform reaches root: Root, Sudo or Sudo with password. The host key is pinned on this first connection.',
      },
      {
        title: 'Fix any reported problems',
        body: 'If the account cannot become root, the row says what to change: add its sudo password or allow passwordless sudo. Distributions that playbooks do not support are flagged too, although commands still run there. After a change, choose Test: the row shows whether the server connected and what was found, or why it could not connect.',
      },
    ],
  },
  {
    slug: 'run-a-playbook',
    icon: BookOpen,
    title: 'Run a Playbook',
    seoTitle: 'How to Run a Playbook on Linux Servers',
    summary:
      'Update packages, install software, harden SSH or free disk space on one or many servers, with a dry run and a preflight check on each server.',
    before: 'At least one connected server.',
    steps: [
      {
        title: 'Open Playbooks and choose a playbook',
        body: 'Playbooks is under Automation in the sidebar, grouped by purpose. Each card lists the distributions it supports and when it last ran.',
      },
      {
        title: 'Set the options',
        body: 'Most playbooks have a few options, such as a PHP version and extensions, the services to restart, or how much of the journal to keep. Every option has a default.',
      },
      {
        title: 'Choose the servers',
        body: 'Select servers by name, group, tag or operating system, or choose all of them. The list below shows every server your selection includes and any known problem that would stop the playbook there, before any server is contacted.',
      },
      {
        title: 'Choose how it rolls out',
        body: 'Try one server first is on by default: one server runs the playbook, and the run waits for you before continuing. At a time sets the batch size, and Stop after sets how many failures to allow before no further batch starts.',
      },
      {
        title: 'Dry run, then run',
        body: 'Dry run reports what would change on every server without changing anything. When the results look right, choose Run. The run opens and shows each server’s preflight, steps and output as they happen.',
      },
    ],
  },
  {
    slug: 'run-a-command',
    icon: SquareTerminal,
    title: 'Run a Command',
    seoTitle: 'How to Run a Command on Multiple Servers',
    summary:
      'Run one shell command on the servers you choose, follow each server’s output as it arrives, and review every exit code afterwards.',
    before: 'At least one connected server, and a command that does not wait for input.',
    steps: [
      {
        title: 'Open Command Runner',
        body: 'Command Runner is under Automation. It is not a terminal: the command runs to completion on each server exactly as typed, and a command that waits for input receives none. During the free trial, the page shows how many of its 25 runs are left.',
      },
      {
        title: 'Enter the command and choose the account',
        body: 'Choose Root to run it as root, directly or through sudo, or Signed-in account to run it as the account the platform connects with. Do not include passwords, tokens or keys: commands and their output are kept in the run history.',
      },
      {
        title: 'Set a time limit',
        body: 'Choose between one minute and one hour per server. A command still running at the limit is stopped on the server itself.',
      },
      {
        title: 'Choose servers and run',
        body: 'Select servers the same way as for a playbook: by server, group, tag, operating system or all. Commands have no dry run, so keeping Try one server first on matters most here.',
      },
      {
        title: 'Run it again later',
        body: 'Open a finished command run and choose Run again. The command, account and time limit are filled in, and you choose the servers again.',
      },
    ],
  },
  {
    slug: 'prepare-a-server-with-a-template',
    icon: LayoutTemplate,
    title: 'Use a Server Template',
    seoTitle: 'How to Set Up a Server with a Template',
    summary:
      'Prepare a WordPress, Laravel, Node.js or Python server, an Nginx reverse proxy with Let’s Encrypt, or a CI runner host in one run.',
    before:
      'A connected server running a distribution the template’s steps support. For the reverse proxy template, domains that already point at the server, and ports 80 and 443 open.',
    steps: [
      {
        title: 'Open Templates and choose one',
        body: 'Templates is under Automation. Each template lists its steps in order and its version, and the version history shows what changed.',
      },
      {
        title: 'Set the options',
        body: 'Choose a PHP, Node.js or Python version and any optional parts, such as a database server or Redis for Laravel. The Python Production Stack also asks for the Application name, the Application module and the Application interface: ASGI for FastAPI or Starlette, WSGI for Django or Flask. Optional steps run only when selected.',
      },
      {
        title: 'For a reverse proxy, point the domains first',
        body: 'Nginx Reverse Proxy + Auto-Renewing SSL requests a real Let’s Encrypt certificate, so every name in Domains must already resolve to the server. Enter where to Forward requests to, an Email for Let’s Encrypt, and accept the Subscriber Agreement. Use a staging certificate to test the setup without using up Let’s Encrypt’s limits.',
      },
      {
        title: 'Choose servers, dry run, then run',
        body: 'Every step’s playbook checks that it supports each server before anything changes. Steps run in order on each server, and a failed step skips the steps after it on that server.',
      },
      {
        title: 'Finish the setup yourself',
        body: 'A template prepares the server. For the Python Production Stack, deploy your code, install its requirements and enable the service, as the end of the run describes. For a CI runner host, register the runner with the command the run ends with and a token from GitHub or GitLab.',
      },
    ],
  },
  {
    slug: 'harden-a-server',
    icon: BrickWall,
    title: 'Harden a Server',
    seoTitle: 'How to Set Up Fail2Ban and a Firewall on Linux',
    summary:
      'Turn on Fail2Ban, a firewall and safer SSH settings with one playbook, starting with a dry run, without losing SSH access to the server.',
    before:
      'A connected server running Ubuntu 22.04 or later, Debian 12 or later, RHEL-compatible 8 or later, or Amazon Linux 2023 with Fail2Ban turned off.',
    steps: [
      {
        title: 'Open the Advanced Security playbook',
        body: 'Playbooks is under Automation. Advanced Security: Fail2Ban & Firewall Hardening is in the Security group.',
      },
      {
        title: 'Choose the firewall and ports',
        body: 'Firewall either turns on the firewall, allowing SSH and the ports you list, or leaves it as it is. Allow ports that services are listening on now keeps everything reachable today reachable; turn it off to allow only SSH and Also allow these ports, such as 80 and 443. SSH is always allowed.',
      },
      {
        title: 'Set up Fail2Ban',
        body: 'Install Fail2Ban for SSH is on by default. Fail2Ban rule sets how quickly an address is banned and for how long. Add office or VPN addresses to Never ban these addresses; the address the platform connects from is always included.',
      },
      {
        title: 'Decide on SSH logins',
        body: 'Apply safe SSH settings does not change how anyone signs in. Password logins and Root logins stay as they are unless you choose otherwise, and each choice is refused while the platform itself signs in that way, so switch the server’s credential to a key first.',
      },
      {
        title: 'Dry run, then run',
        body: 'Dry run shows the firewall it found, the ports it would allow and anything it would block. If a check fails during the real run, every change is put back before the step ends, and a run that is cut off is undone by the server itself within five minutes.',
      },
      {
        title: 'Check firewalls outside the server',
        body: 'Cloud security groups and provider firewalls are not changed, so open or close ports there separately. Ports that Docker publishes stay reachable whatever the host firewall allows.',
      },
    ],
  },
  {
    slug: 'follow-a-run',
    icon: History,
    title: 'Manage a Run',
    seoTitle: 'How to Confirm, Cancel or Retry a Run',
    summary:
      'Review each server’s results, continue after the canary server, cancel a run, or retry only the servers that did not succeed.',
    before: 'A run you started from a playbook, a template or the command runner.',
    steps: [
      {
        title: 'Open the run',
        body: 'Starting a run opens it. Later, find it under Runs in Automation, which lists every run newest first and can be filtered by type and status. A server’s page also links to the runs that touched it.',
      },
      {
        title: 'Review each server',
        body: 'Servers are listed by status. Open one to see its preflight checks, each step with its outcome and attempts, and its output as it arrives. Download log saves that server’s output as a text file.',
      },
      {
        title: 'Confirm the canary',
        body: 'When the first server succeeds, the run pauses and says so at the top. Review that server’s output, then choose Continue on the remaining servers. If nobody confirms within 24 hours, the run is cancelled and the other servers are left untouched.',
      },
      {
        title: 'Cancel a run',
        body: 'Cancel run stops work on servers that are still running and skips servers that have not started. Everything that already happened stays recorded.',
      },
      {
        title: 'Retry failed servers',
        body: 'On a finished run, choose Retry servers that did not succeed. A new run starts for the failed, blocked, skipped and cancelled servers only, linked to the original. Fix anything the preflight reported first.',
      },
    ],
  },
  {
    slug: 'monitor-a-site',
    icon: Radar,
    title: 'Create a Monitor',
    seoTitle: 'How to Monitor a Website, Service or Server',
    summary:
      'Set up website, port, SSL, DNS, domain and server health checks that run from outside your network, try a safe fix first, and alert you if it does not work.',
    before: 'A public address to check, or a connected server for a server health check.',
    steps: [
      {
        title: 'Open Monitoring and choose New monitor',
        body: 'Monitoring is under Operations. Checks run from the platform, not from your server, so they can detect a server that is completely unreachable.',
      },
      {
        title: 'Choose what to check',
        body: 'Website or API requests a page and checks the response. Port checks that a service accepts connections. SSL certificate watches an expiry date. DNS record checks that a record still contains the expected value. Domain expiry checks the registration with the registry. Server health watches disk, memory and load on a connected server.',
      },
      {
        title: 'Set the expected result and interval',
        body: 'A website check can require a phrase on the page, which catches an application that fails while the server still responds. The shortest interval is one minute. A failure must repeat before it counts, so a single blip does not trigger an alert.',
      },
      {
        title: 'Attach a server for Auto-Fix',
        body: 'With a server selected, Try to fix this automatically lets the platform restart stopped services, reload PHP-FPM or the web server, or free disk space before alerting you. It never reboots, deletes data or changes firewall rules, and it stops after three attempts in a day.',
      },
      {
        title: 'Choose where alerts go',
        body: 'Where to send alerts takes email addresses and webhooks, added once and used by every monitor. Without one, a monitor detects outages but notifies nobody.',
      },
      {
        title: 'Review the summary',
        body: 'Up, Down, Not checked recently and Average uptime over the last 24 hours, the same four figures shown on your dashboard. Not checked recently counts monitors whose last check is older than three of their intervals, so their status may be out of date. The average leaves out SSL and domain expiry monitors, which count days rather than uptime, and only includes monitors with at least five checks in the day.',
      },
    ],
  },
  {
    slug: 'set-up-backups',
    icon: Archive,
    title: 'Set Up Backups',
    seoTitle: 'How to Set Up Automatic Server Backups',
    summary:
      'Choose what to back up, where the copies go and how often, then run a first backup. Archives go straight to your storage, never to the platform.',
    before: 'A connected server, and somewhere to store the copies.',
    steps: [
      {
        title: 'Add a backup source',
        body: 'On Backups, under Operations, What is backed up lists your sources. Add source asks for a name, the server, and a directory, a MariaDB, MySQL or PostgreSQL database, or both. No database password is needed: the server’s own administrator access is used.',
      },
      {
        title: 'Add backup storage',
        body: 'Backup storage supports Amazon S3, Backblaze B2, DigitalOcean Spaces, Hetzner Object Storage, Wasabi, Cloudflare R2, any other S3-compatible storage, or the server itself. S3 storage needs an access key and secret. A copy kept on the server is lost if the server is lost, and the page says so.',
      },
      {
        title: 'Create a schedule',
        body: 'Under Automatic backups, choose New schedule: the source, the destination, daily or weekly, and how many copies to keep. A full backup copies the directory and the database; database only is small enough to run more often.',
      },
      {
        title: 'Run a backup now',
        body: 'Back up now starts a backup immediately. It can take a few minutes and appears in Backup history while it runs. A finished backup downloads as .tar.gz directly from your bucket, or as .zip converted on your server first.',
      },
      {
        title: 'Restore a backup',
        body: 'Restore checks the archive against its recorded checksum before changing anything, moves current files aside rather than deleting them, and asks you to type the source name to confirm. A backup in cloud storage can be restored to a different server, which moves the source there.',
      },
    ],
  },
  {
    slug: 'check-a-backup-restores',
    icon: FlaskConical,
    title: 'Test a Backup',
    seoTitle: 'How to Test That a Backup Restores',
    summary:
      'Review the weekly restore tests, test any backup on demand, and understand what passed, failed and could not run each mean for a backup.',
    before: 'At least one successful backup.',
    steps: [
      {
        title: 'Review restore verification',
        body: 'The Restore verification panel on Backups shows the latest restore test, and Each source lists every source’s latest result. Each source’s newest backup is tested every week automatically.',
      },
      {
        title: 'Test a backup now',
        body: 'Test restore on any completed backup fetches it onto its server, verifies it byte for byte, loads its database into a scratch database, reads every table and drops the scratch copy. Your live data is not touched.',
      },
      {
        title: 'Understand the results',
        body: 'Passed means the backup restores. Failed means it would not, and the account is emailed. Could not run means the test was blocked, for example by an unreachable server or low disk space. It says nothing about the backup, and is shown in amber rather than red.',
      },
      {
        title: 'Check the badges in Backup history',
        body: 'Each backup shows how far it has been verified: Confirmed stored, Restore tested or Failed verification. A backup that failed verification no longer counts toward the copies a schedule keeps.',
      },
    ],
  },
  {
    slug: 'invite-your-team',
    icon: Users,
    title: 'Invite Your Team',
    seoTitle: 'How to Invite Team Members',
    summary:
      'Give colleagues their own login to the same servers, automation, monitors and backups, without sharing a password, and remove their access in one step.',
    before: 'A plan that covers more than one person: Pro or Business. Starter and the free trial cover one.',
    steps: [
      {
        title: 'Open Settings, then Team',
        body: 'Everyone on the account can see who else is on it. Only the account owner, the person who created the account, can add or remove people.',
      },
      {
        title: 'Send an invitation',
        body: 'Enter an email address and, optionally, a name. The person receives an email with a link, sets their own password and signs in as themselves. You never see or set their password.',
      },
      {
        title: 'Review what members can do',
        body: 'Members can do everything you can with this account’s servers: run playbooks, templates and commands, confirm or cancel runs, change monitors and restore backups. They cannot manage the team or reach billing. Finer permissions are not available yet.',
      },
      {
        title: 'Remove a member',
        body: 'Removed members are signed out immediately and can no longer reach anything on the account. Their runs stay in the history under their name. You cannot remove yourself, because the team belongs to your account.',
      },
      {
        title: 'Check your plan’s limit',
        body: 'The owner counts as one person, so a plan for five people allows four invitations. The page shows how many remain and stops offering invitations at the limit, which the server also enforces.',
      },
    ],
  },
];

export function guideBySlug(slug: string): Guide | undefined {
  return GUIDES.find((guide) => guide.slug === slug || (guide.aliases ?? []).includes(slug));
}

/** A stable anchor for a step, so the "on this page" rail can link to it. */
export function stepAnchor(title: string): string {
  return title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}
